import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import jsQR from 'jsqr';
import { ChevronRight } from './icons';

type Props = {
  open: boolean;
  hint: string;
  /** Return true to accept the payload and close the overlay. */
  onScan: (text: string) => boolean;
  onClose: () => void;
  onError?: (message: string) => void;
};

/**
 * Browser camera QR scanner for the standalone web wallet.
 * Telegram Mini Apps use the native showScanQrPopup instead.
 */
export function QrScanOverlay({ open, hint, onScan, onClose, onError }: Props) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef(0);
  const onScanRef = useRef(onScan);
  const onCloseRef = useRef(onClose);
  const onErrorRef = useRef(onError);
  onScanRef.current = onScan;
  onCloseRef.current = onClose;
  onErrorRef.current = onError;
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    const stop = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
      const stream = streamRef.current;
      streamRef.current = null;
      stream?.getTracks().forEach((track) => track.stop());
      const video = videoRef.current;
      if (video) video.srcObject = null;
    };

    const tick = () => {
      if (cancelled) return;
      const video = videoRef.current;
      if (video && ctx && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        const w = video.videoWidth;
        const h = video.videoHeight;
        if (w > 0 && h > 0) {
          // Downscale large frames — jsQR is CPU-bound on phones.
          const maxSide = 640;
          const scale = Math.min(1, maxSide / Math.max(w, h));
          const cw = Math.max(1, Math.round(w * scale));
          const ch = Math.max(1, Math.round(h * scale));
          if (canvas.width !== cw || canvas.height !== ch) {
            canvas.width = cw;
            canvas.height = ch;
          }
          ctx.drawImage(video, 0, 0, cw, ch);
          const image = ctx.getImageData(0, 0, cw, ch);
          const code = jsQR(image.data, cw, ch, { inversionAttempts: 'dontInvert' });
          if (code?.data && onScanRef.current(code.data)) {
            stop();
            onCloseRef.current();
            return;
          }
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    const start = async () => {
      setStarting(true);
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          onErrorRef.current?.(t('send.scanUnavailable'));
          onCloseRef.current();
          return;
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { facingMode: { ideal: 'environment' } },
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        video.srcObject = stream;
        await video.play();
        rafRef.current = requestAnimationFrame(tick);
      } catch {
        onErrorRef.current?.(t('send.scanCameraDenied'));
        onCloseRef.current();
      } finally {
        if (!cancelled) setStarting(false);
      }
    };

    void start();

    return () => {
      cancelled = true;
      stop();
    };
  }, [open, t]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex flex-col bg-black">
      <video
        ref={videoRef}
        className="absolute inset-0 h-full w-full object-cover"
        playsInline
        muted
        autoPlay
      />
      <div className="absolute inset-0 bg-black/35" />

      <div className="relative z-10 flex items-center justify-between px-4 pt-[calc(var(--tg-safe-top)_+_0.75rem)]">
        <button
          type="button"
          onClick={onClose}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-black/45 text-white"
          aria-label={t('common.cancel')}
        >
          <ChevronRight size={18} className="rotate-180 text-white" />
        </button>
      </div>

      <div className="relative z-10 flex flex-1 flex-col items-center justify-center px-8">
        <div className="aspect-square w-full max-w-[280px] rounded-[28px] border-2 border-white/85 shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]" />
        <p className="mt-6 max-w-[280px] text-center text-[14px] font-medium leading-snug text-white">
          {starting ? t('send.scanStarting') : hint}
        </p>
      </div>

      <div className="relative z-10 pb-[max(1.5rem,var(--tg-safe-bottom))]" />
    </div>
  );
}
